// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Centralized Error Handling System
 * Provides consistent error types, handling, and reporting across the application
 */

import type { AppError, ValidationError, ProcessingError } from '@/types';

// Error codes for different error types
export const ERROR_CODES = {
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONNECTION_TIMEOUT: 'CONNECTION_TIMEOUT',
  API_ERROR: 'API_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',

  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',

  // Upload errors
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  UPLOAD_CANCELLED: 'UPLOAD_CANCELLED',
  UPLOAD_TIMEOUT: 'UPLOAD_TIMEOUT',
  STORAGE_QUOTA_EXCEEDED: 'STORAGE_QUOTA_EXCEEDED',

  // Video processing errors
  PROCESSING_FAILED: 'PROCESSING_FAILED',
  UNSUPPORTED_FORMAT: 'UNSUPPORTED_FORMAT',
  CORRUPTED_FILE: 'CORRUPTED_FILE',
  TRANSCRIPTION_FAILED: 'TRANSCRIPTION_FAILED',

  // YouTube errors
  INVALID_YOUTUBE_URL: 'INVALID_YOUTUBE_URL',
  VIDEO_NOT_FOUND: 'VIDEO_NOT_FOUND',
  VIDEO_PRIVATE: 'VIDEO_PRIVATE',
  YOUTUBE_API_ERROR: 'YOUTUBE_API_ERROR',

  // Database errors
  DATABASE_ERROR: 'DATABASE_ERROR',
  RECORD_NOT_FOUND: 'RECORD_NOT_FOUND',
  DUPLICATE_RECORD: 'DUPLICATE_RECORD',
  CONSTRAINT_VIOLATION: 'CONSTRAINT_VIOLATION',

  // Configuration errors
  CONFIG_ERROR: 'CONFIG_ERROR',
  MISSING_API_KEY: 'MISSING_API_KEY',
  INVALID_CONFIG: 'INVALID_CONFIG',

  // Generic errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  CANCELLED: 'CANCELLED'
} as const;

export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES];

/**
 * Enhanced AppError class with additional functionality
 */
export class ApplicationError extends Error implements AppError {
  public readonly code: ErrorCode;
  public readonly details?: any;
  public readonly timestamp: number;
  public readonly userId?: string;
  public readonly requestId?: string;
  public readonly stackTrace?: string;
  public readonly recoverable: boolean;
  public readonly userMessage?: string;

  constructor({
    code,
    message,
    details,
    userId,
    requestId,
    recoverable = false,
    userMessage,
    cause
  }: {
    code: ErrorCode;
    message: string;
    details?: any;
    userId?: string;
    requestId?: string;
    recoverable?: boolean;
    userMessage?: string;
    cause?: Error;
  }) {
    // Use options parameter only if cause is provided
    // @ts-expect-error - ES2022 Error options supported at runtime
    super(message, cause !== undefined ? { cause } : undefined);

    this.name = this.constructor.name;
    this.code = code;
    this.details = details;
    this.timestamp = Date.now();
    if (userId !== undefined) this.userId = userId;
    if (requestId !== undefined) this.requestId = requestId;
    if (this.stack !== undefined) this.stackTrace = this.stack;
    this.recoverable = recoverable;
    if (userMessage !== undefined) this.userMessage = userMessage;

    // Ensure the stack trace is properly captured
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Convert error to JSON for logging/API responses
   */
  toJSON(): AppError {
    const result: AppError = {
      code: this.code,
      message: this.message,
      timestamp: this.timestamp,
    };
    if (this.details !== undefined) result.details = this.details;
    if (this.userId !== undefined) result.userId = this.userId;
    if (this.requestId !== undefined) result.requestId = this.requestId;
    if (this.stackTrace !== undefined) result.stack = this.stackTrace;
    return result;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    return this.userMessage || this.getDefaultUserMessage();
  }

  /**
   * Get default user message based on error code
   */
  private getDefaultUserMessage(): string {
    switch (this.code) {
      case ERROR_CODES.NETWORK_ERROR:
      case ERROR_CODES.CONNECTION_TIMEOUT:
        return 'Network connection error. Please check your internet connection and try again.';

      case ERROR_CODES.UNAUTHORIZED:
        return 'You need to be logged in to perform this action.';

      case ERROR_CODES.FORBIDDEN:
        return 'You don\'t have permission to perform this action.';

      case ERROR_CODES.TOKEN_EXPIRED:
        return 'Your session has expired. Please log in again.';

      case ERROR_CODES.VALIDATION_ERROR:
        return 'Please check your input and try again.';

      case ERROR_CODES.INVALID_FILE_TYPE:
        return 'This file type is not supported. Please upload a valid video or audio file.';

      case ERROR_CODES.FILE_TOO_LARGE:
        return 'The file is too large. Please upload a smaller file.';

      case ERROR_CODES.UPLOAD_FAILED:
        return 'Upload failed. Please try again.';

      case ERROR_CODES.PROCESSING_FAILED:
        return 'Video processing failed. Please try uploading again.';

      case ERROR_CODES.INVALID_YOUTUBE_URL:
        return 'Invalid YouTube URL. Please check the URL and try again.';

      case ERROR_CODES.VIDEO_NOT_FOUND:
        return 'YouTube video not found. Please check the URL and try again.';

      case ERROR_CODES.STORAGE_QUOTA_EXCEEDED:
        return 'You have exceeded your storage quota. Please upgrade your plan or delete some files.';

      default:
        return 'An error occurred. Please try again or contact support if the problem persists.';
    }
  }

  /**
   * Check if error is recoverable (user can retry)
   */
  isRecoverable(): boolean {
    return this.recoverable || this.getDefaultRecoverability();
  }

  /**
   * Get default recoverability based on error code
   */
  private getDefaultRecoverability(): boolean {
    const recoverableErrors: readonly ErrorCode[] = [
      ERROR_CODES.NETWORK_ERROR,
      ERROR_CODES.CONNECTION_TIMEOUT,
      ERROR_CODES.UPLOAD_FAILED,
      ERROR_CODES.PROCESSING_FAILED,
      ERROR_CODES.TRANSCRIPTION_FAILED,
      ERROR_CODES.YOUTUBE_API_ERROR,
      ERROR_CODES.SERVER_ERROR
    ];

    return recoverableErrors.includes(this.code);
  }
}

/**
 * Validation Error for input validation failures
 */
export class ApplicationValidationError extends ApplicationError implements ValidationError {
  public readonly field: string;
  public readonly value: any;
  public readonly constraint: string;

  constructor({
    field,
    value,
    constraint,
    message,
    userMessage
  }: {
    field: string;
    value: any;
    constraint: string;
    message?: string;
    userMessage?: string;
  }) {
    super({
      code: ERROR_CODES.VALIDATION_ERROR,
      message: message || `Validation failed for field '${field}'`,
      details: { field, value, constraint },
      userMessage: userMessage || `Invalid value for ${field}. ${constraint}`
    });

    this.field = field;
    this.value = value;
    this.constraint = constraint;
  }
}

/**
 * Processing Error for background job failures
 */
export class ApplicationProcessingError extends ApplicationError implements ProcessingError {
  public readonly jobId?: string;
  public readonly retryable: boolean;
  public readonly retryAfter?: number;

  constructor({
    jobId,
    retryable = true,
    retryAfter,
    message,
    details,
    userMessage
  }: {
    jobId?: string;
    retryable?: boolean;
    retryAfter?: number;
    message?: string;
    details?: any;
    userMessage?: string;
  }) {
    // Build details object conditionally to avoid undefined values
    const processedDetails: Record<string, unknown> = { retryable };
    if (jobId !== undefined) processedDetails.jobId = jobId;
    if (retryAfter !== undefined) processedDetails.retryAfter = retryAfter;
    if (details !== undefined) Object.assign(processedDetails, details);

    super({
      code: ERROR_CODES.PROCESSING_FAILED,
      message: message || 'Processing operation failed',
      details: processedDetails,
      userMessage: userMessage || 'Processing failed. Please try again.',
      recoverable: retryable
    });

    if (jobId !== undefined) this.jobId = jobId;
    this.retryable = retryable;
    if (retryAfter !== undefined) this.retryAfter = retryAfter;
  }
}

/**
 * Error factory functions for common error scenarios
 */
export const ErrorFactory = {
  networkError(message?: string, details?: any): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.NETWORK_ERROR,
      message: message || 'Network connection failed',
      details,
      recoverable: true,
      userMessage: 'Network connection error. Please check your internet connection.'
    });
  },

  validationError(field: string, value: any, constraint: string): ApplicationValidationError {
    return new ApplicationValidationError({
      field,
      value,
      constraint,
      message: `Validation failed for ${field}: ${constraint}`,
      userMessage: `Invalid ${field}. ${constraint}`
    });
  },

  unauthorized(message?: string): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.UNAUTHORIZED,
      message: message || 'User not authenticated',
      userMessage: 'Please log in to continue.'
    });
  },

  forbidden(message?: string): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.FORBIDDEN,
      message: message || 'Access denied',
      userMessage: 'You don\'t have permission to perform this action.'
    });
  },

  uploadFailed(message?: string, details?: any): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.UPLOAD_FAILED,
      message: message || 'File upload failed',
      details,
      recoverable: true,
      userMessage: 'Upload failed. Please try again.'
    });
  },

  fileTooLarge(maxSize: number, actualSize: number): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.FILE_TOO_LARGE,
      message: `File size ${actualSize} exceeds maximum ${maxSize}`,
      details: { maxSize, actualSize },
      userMessage: `File is too large. Maximum size is ${Math.round(maxSize / (1024 * 1024))}MB.`
    });
  },

  invalidFileType(fileType: string, allowedTypes: string[]): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.INVALID_FILE_TYPE,
      message: `Invalid file type: ${fileType}`,
      details: { fileType, allowedTypes },
      userMessage: `File type ${fileType} is not supported. Allowed types: ${allowedTypes.join(', ')}.`
    });
  },

  invalidYouTubeUrl(url: string): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.INVALID_YOUTUBE_URL,
      message: `Invalid YouTube URL: ${url}`,
      details: { url },
      userMessage: 'Invalid YouTube URL. Please check the URL and try again.'
    });
  },

  processingFailed(jobId?: string, retryable: boolean = true): ApplicationProcessingError {
    const options: {
      jobId?: string;
      retryable?: boolean;
      retryAfter?: number;
      message?: string;
      details?: unknown;
      userMessage?: string;
    } = {
      retryable,
      message: 'Background processing job failed',
      userMessage: retryable ? 'Processing failed. You can try again.' : 'Processing failed permanently.'
    };
    if (jobId !== undefined) options.jobId = jobId;
    return new ApplicationProcessingError(options);
  },

  storageQuotaExceeded(current: number, limit: number): ApplicationError {
    return new ApplicationError({
      code: ERROR_CODES.STORAGE_QUOTA_EXCEEDED,
      message: `Storage quota exceeded: ${current} > ${limit}`,
      details: { current, limit },
      userMessage: 'You have exceeded your storage quota. Please upgrade your plan or delete some files.'
    });
  },

  apiError(statusCode: number, message?: string, details?: any): ApplicationError {
    const code = statusCode >= 500 ? ERROR_CODES.SERVER_ERROR : ERROR_CODES.API_ERROR;
    return new ApplicationError({
      code,
      message: message || `API error: ${statusCode}`,
      details: { statusCode, ...details },
      recoverable: statusCode < 500,
      userMessage: statusCode >= 500
        ? 'Server error. Please try again later.'
        : 'Request failed. Please try again.'
    });
  },
};

/**
 * Error handler utility functions
 */
export const ErrorHandler = {
  /**
   * Convert any error to ApplicationError
   */
  normalize(error: any): ApplicationError {
    if (error instanceof ApplicationError) {
      return error;
    }

    if (error instanceof Error) {
      // Check for common error patterns
      if (error.message.includes('fetch')) {
        return ErrorFactory.networkError(error.message);
      }

      if (error.message.includes('timeout')) {
        return new ApplicationError({
          code: ERROR_CODES.CONNECTION_TIMEOUT,
          message: error.message,
          recoverable: true,
          userMessage: 'Request timed out. Please try again.'
        });
      }

      // Generic error conversion
      return new ApplicationError({
        code: ERROR_CODES.UNKNOWN_ERROR,
        message: error.message,
        details: error.stack ? { originalStack: error.stack } : undefined,
        userMessage: 'An unexpected error occurred.'
      });
    }

    // Handle string errors
    if (typeof error === 'string') {
      return new ApplicationError({
        code: ERROR_CODES.UNKNOWN_ERROR,
        message: error,
        userMessage: 'An error occurred.'
      });
    }

    // Handle unknown error types
    return new ApplicationError({
      code: ERROR_CODES.UNKNOWN_ERROR,
      message: 'Unknown error occurred',
      details: error,
      userMessage: 'An unexpected error occurred.'
    });
  },

  /**
   * Check if error should be reported to monitoring service
   */
  shouldReport(error: ApplicationError): boolean {
    const nonReportableCodes: readonly ErrorCode[] = [
      ERROR_CODES.VALIDATION_ERROR,
      ERROR_CODES.UNAUTHORIZED,
      ERROR_CODES.FORBIDDEN,
      ERROR_CODES.TOKEN_EXPIRED,
      ERROR_CODES.CANCELLED
    ];

    return !nonReportableCodes.includes(error.code);
  },

  /**
   * Log error with context
   */
  log(error: ApplicationError, context?: Record<string, any>): void {
    const logData = {
      error: error.toJSON(),
      context,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      timestamp: Date.now()
    };

    if (process.env.NODE_ENV === 'development') {
      console.error('[Error]', logData);
    } else {
      // In production, send to monitoring service
      ErrorHandler.sendToMonitoring(logData);
    }
  },

  /**
   * Send error to monitoring service (placeholder implementation)
   */
  sendToMonitoring(logData: any): void {
    // Integration with monitoring service like Sentry, LogRocket, etc.
    // This would be implemented based on the chosen monitoring solution
    console.warn('Monitoring service integration needed:', logData);
  },
};

/**
 * Error boundary component props
 */
export interface ErrorBoundaryState {
  hasError: boolean;
  error: ApplicationError | null;
  errorInfo: any;
}

/**
 * Error boundary fallback component props
 */
export interface ErrorFallbackProps {
  error: ApplicationError;
  resetError: () => void;
  componentStack?: string | undefined;
}

// Re-export types for convenience
export type { AppError, ValidationError, ProcessingError } from '@/types';